// Avoid having an export on the first line for istanbul

interface CachedItem {
  wasCached: boolean;
}

// If you're looking at adding a new usage of this, it's probably time to find a good
// node package. This works well and is lightweight, but we probably shouldn't care
// about maintaining our own caching library.
export function createLruCache<TCacheData extends Record<string, any>>(
  maxCacheSize: number,
) {
  let cache: Record<string, TCacheData | undefined> = {};
  let savedKeys: string[] = [];

  const set = (key: string, value: TCacheData) => {
    if (maxCacheSize > 0) {
      savedKeys = savedKeys.filter((savedKey) => savedKey !== key);
      savedKeys.push(key);
    }
    while (savedKeys.length > maxCacheSize) {
      const lruKey = savedKeys.shift();
      if (lruKey !== undefined) {
        delete cache[lruKey];
      }
    }
    cache[key] = value;
  };

  const get = (
    key: string,
    populateCacheMiss: () => TCacheData,
  ): TCacheData & CachedItem => {
    const wasCached = key in cache;
    const result = cache[key] || populateCacheMiss?.();
    // This allows us to cached undefined if explicitly populated or set,
    // but not on a cache miss otherwise.
    if (result) {
      set(key, result);
    }
    return { ...result, wasCached };
  };

  const has = (key: string): boolean => {
    return key in cache;
  };

  const clear = () => {
    cache = {};
    savedKeys = [];
  };

  return {
    has,
    get,
    set,
    clear,
  };
}
